import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import DifferentiationHierarchyPanel from "../studentDifferentiation/PanelUI/DifferentiationHierarchyPanel";
import ElementaryProgressReportDao from "../../../dao/ElementaryProgressReportDao";
import TabButton from "../../formInputs/buttons/TabButton";

import { GlobalContext } from "../../contextProvider/ContextProvider";
import { gradeTranslation, subjectIdToContentAreaId } from "../../../utils/SchoolAdmin/translations";
import { useReportableHierarchy } from "../../../utils/TemplateToReportableHierarchy";

import "../../../styles/SetupDifferentiation.scss";

/**
 * Step 3: Add differentiated items from higher grades
 */
const SetupAddDiffItems = ({ selectedTemplate, selectedGrade, addedItems, setAddedItems }) => {
    const { state: globalState } = useContext(GlobalContext);
    const { currentTimePeriodDto, token } = globalState || {};

    const [activeGrade, setActiveGrade] = useState("");
    const [reportableContent, setReportableContent] = useState(null);

    const displayToTag = useMemo(
        () => Object.fromEntries(Object.entries(gradeTranslation).map(([tag, display]) => [display, tag])),
        []
    );

    const eligibleGradesString = useMemo(() => {
        const allGrades = ["K", "FIRST", "SECOND", "THIRD", "FOURTH", "FIFTH", "SIXTH", "SEVENTH", "EIGHTH"];
        const isSecondSemester = currentTimePeriodDto?.name === "2nd Semester";
        const currentTag = displayToTag[selectedGrade];
        const startIdx = currentTag ? allGrades.indexOf(currentTag) : -1;

        if (startIdx === -1) return allGrades.join(",");

        const adjustedStart = isSecondSemester ? startIdx + 1 : startIdx;
        return allGrades.slice(adjustedStart).join(",");
    }, [selectedGrade, currentTimePeriodDto, displayToTag]);

    const eligibleGrades = useMemo(() => eligibleGradesString.split(","), [eligibleGradesString]);

    // Fetch reportable content and auto-select first grade
    useEffect(() => {
        if (!selectedTemplate || !eligibleGradesString || !token) return;

        const options = {
            action: "eprReportableByContentAreaAndGrades",
            contentAreaKey: subjectIdToContentAreaId[selectedTemplate.subjectId],
            gradeLevels: eligibleGradesString.replaceAll(",", ", "),
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    setReportableContent(response.data.payload);
                    if (eligibleGrades.length > 0 && !activeGrade) {
                        setActiveGrade(eligibleGrades[0]);
                    }
                }
            })
            .catch(console.error);
        // Note: activeGrade and eligibleGrades intentionally excluded from deps to avoid infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplate, eligibleGradesString, token]);

    const activeReportableContent = useMemo(
        () => reportableContent?.gradeLevels?.filter((grade) => grade.gradeLevel === activeGrade) || [{}],
        [reportableContent, activeGrade]
    );

    const { tree: diffTree } = useReportableHierarchy(activeReportableContent[0], "CONTENT");

    const makeKey = (n) => `${n.contentType}:${n.contentId}`;

    const handleToggle = (node) => {
        const key = makeKey(node);
        setAddedItems((prev) => {
            const exists = prev?.some((p) => makeKey(p) === key);
            return exists ? prev?.filter((p) => makeKey(p) !== key) : [...prev, node];
        });
    };

    const selectedKeys = useMemo(() => new Set(addedItems?.map(makeKey)), [addedItems]);

    return (
        <div>
            <h5 className="setup-step-title">Add Differentiated Items</h5>
            <div className="setup-step-directions">
                Select content items from the grades below to add for the selected student(s). Content items that you
                add will become ratable and will appear on the final progress report for the student(s) along with any
                non-excluded grade-level content items that include ratings.
            </div>

            <div className="mb-3">
                <strong>Select a Grade Level:</strong>
                <div className="d-flex gap-2 mt-2 flex-wrap">
                    {eligibleGrades.map((gradeTag) => {
                        const isSelected = activeGrade === gradeTag;
                        return (
                            <TabButton
                                key={gradeTag}
                                className={isSelected ? "tab-button-selected" : "tab-button"}
                                onClick={() => setActiveGrade(gradeTag)}
                                size="small"
                            >
                                <p className="primary m-0">{gradeTranslation[gradeTag]}</p>
                            </TabButton>
                        );
                    })}
                </div>
            </div>

            {activeGrade && activeReportableContent.length > 0 && (
                <div style={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px" }}>
                    <DifferentiationHierarchyPanel
                        dataType="DIFF"
                        selectedTemplate={selectedTemplate}
                        tree={diffTree}
                        selectedKeys={selectedKeys}
                        selectedCount={addedItems?.length || 0}
                        onToggle={handleToggle}
                    />
                </div>
            )}

            {!activeGrade && (
                <div className="text-center text-muted p-4">Select a grade level to view available content items.</div>
            )}
        </div>
    );
};

SetupAddDiffItems.propTypes = {
    selectedTemplate: PropTypes.object.isRequired,
    selectedGrade: PropTypes.string.isRequired,
    addedItems: PropTypes.array.isRequired,
    setAddedItems: PropTypes.func.isRequired
};

export default SetupAddDiffItems;
